import React, { useState, useEffect, useCallback, useRef } from "react";
import apiClient from "../../api_client";
import debounce from "lodash/debounce";
import {
  Table,
  Checkbox,
  message,
  Card,
  Row,
  Col,
  Input,
  Button,
  Spin,
  Tooltip,
} from "antd";
import { API_URL } from "../../url_config";
import { NavigationStepsKeolisNoc } from "../../components/navbars/navbar_keolis_noc";
import { EditOutlined, ReloadOutlined } from "@ant-design/icons";
import ModifyItemPage from "../../components/modify_page";
import CoquillesTable from "../../components/counter";

const { TextArea } = Input;

// === Styles tableau ===
const tableStyle = document.createElement("style");
tableStyle.innerHTML = `
  .custom-row td {
    border-bottom: 2px solid #d9d9d9 !important;
  }

  .ant-table-thead > tr > th {
    background-color: #f5f5f5 !important;
    font-weight: 600;
    border-bottom: 2px solid #bfbfbf !important;
    color: #333;
  }

  .ant-table-tbody > tr:hover > td {
    background-color: #f0f0f0 !important;
  }

  .ant-table {
    border-collapse: separate !important;
    border-spacing: 0;
  }

  .comment-cell textarea {
    width: 100%;
    resize: none;
    border-radius: 6px;
  }
`;
document.head.appendChild(tableStyle);

// === Composant de cellule fluide ===
const EditableTextCell = ({ recordId, initialValue, field, onPatch, rows = 4 }) => {
  const [value, setValue] = useState(initialValue || "");

  useEffect(() => {
    setValue(initialValue || "");
  }, [initialValue]);

  const debouncedSave = useCallback(
    debounce((val) => {
      onPatch(recordId, field, val);
    }, 800),
    [recordId, field, onPatch]
  );

  const handleChange = (e) => {
    const val = e.target.value;
    setValue(val);
    debouncedSave(val);
  };

  return (
    <TextArea
      rows={rows}
      value={value}
      onChange={handleChange}
      placeholder="Saisissez un commentaire..."
      autoSize={{ minRows: rows, maxRows: 10 }}
    />
  );
};

// === Ligne développée (comment + aide) ===
function TableCoquilleExpandable({ record, onPatch }) {
  return (
    <div style={{ padding: "20px" }}>
      <Row gutter={16}>
        <Col span={12}>
          <Card title="Commentaire" bordered>
            <EditableTextCell
              recordId={record.id}
              initialValue={record.comment}
              field="comment"
              onPatch={onPatch}
            />
          </Card>
        </Col>
        <Col span={12}>
          <Card title="Informations (Supervision)" bordered>
            <EditableTextCell
              recordId={record.id}
              initialValue={record.aide}
              field="aide"
              onPatch={onPatch}
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
}

// === Composant principal ===
const ValidationCoquilleConfigDspKeolis = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const pendingUpdatesRef = useRef(new Map());
  const timeoutRefs = useRef(new Map());

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await apiClient.get(
        `${API_URL}/validations/validation_coquille_config_dsp/?ot_tag=keolis_noc`
      );
      const formatted = res.data.map((item, index) => ({
        key: item.id || index,
        id: item.id,
        dsp: item.dsp,
        coquille: item.coquille,
        validation_sortie_coquille: item.validation_sortie_coquille,
        commentaire: item.commentaire,
        comment: item.comment,
        aide: item.aide,
        signal: item.signal,
        ot_tag: item.ot_tag,
      }));
      setData(formatted);
    } catch (error) {
      console.error(error);
      message.error("Erreur de chargement des données");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // === Fonction pour envoyer une mise à jour ===
  const sendUpdate = async (updateKey) => {
    const update = pendingUpdatesRef.current.get(updateKey);
    if (!update) return;

    try {
      await apiClient.patch(
        `${API_URL}/validations/validation_coquille_config_dsp/${update.id}/`,
        { [update.field]: update.value }
      );
      const current = pendingUpdatesRef.current.get(updateKey);
      if (current && current.timestamp === update.timestamp) {
        pendingUpdatesRef.current.delete(updateKey);
      }
    } catch (error) {
      console.error(error);
      message.error("Erreur de mise à jour");
    }
  };

  // === Fonction pour envoyer une mise à jour (utilisée par EditableTextCell et TableCoquilleExpandable) ===
  const handlePatch = (id, field, value) => {
    const updateKey = `${id}_${field}`;
    
    const existingTimeout = timeoutRefs.current.get(updateKey);
    if (existingTimeout) {
      clearTimeout(existingTimeout);
    }
    
    pendingUpdatesRef.current.set(updateKey, { 
      id, 
      field, 
      value,
      timestamp: Date.now()
    });
    
    const timeoutId = setTimeout(() => {
      sendUpdate(updateKey);
      timeoutRefs.current.delete(updateKey);
    }, 1000);
    
    timeoutRefs.current.set(updateKey, timeoutId);
  };

  // === Mise à jour locale immédiate + PATCH avec debounce ===
  const handleLocalChange = (key, field, value, recordId) => {
    setData((prev) =>
      prev.map((item) => (item.key === key ? { ...item, [field]: value } : item))
    );

    if (recordId) {
      const updateKey = `${recordId}_${field}`;
      
      const existingTimeout = timeoutRefs.current.get(updateKey);
      if (existingTimeout) {
        clearTimeout(existingTimeout);
      }
      
      pendingUpdatesRef.current.set(updateKey, { 
        id: recordId, 
        field, 
        value,
        timestamp: Date.now()
      });
      
      const timeoutId = setTimeout(() => {
        sendUpdate(updateKey);
        timeoutRefs.current.delete(updateKey);
      }, 1000);
      
      timeoutRefs.current.set(updateKey, timeoutId);
    }
  };

  // Filtrer les données selon la recherche (sauf commentaire et informations)
  const filteredData = data.filter((item) => {
    if (!searchText) return true;
    const search = searchText.toLowerCase();
    return (
      (item.dsp?.toString().toLowerCase() || "").includes(search) ||
      (item.coquille?.toString().toLowerCase() || "").includes(search)
    );
  });

  const columns = [
    { 
      title: "DSP", 
      dataIndex: "dsp",
      sorter: (a, b) => (a.dsp || "").localeCompare(b.dsp || ""),
    },
    { 
      title: "Coquille", 
      dataIndex: "coquille",
      sorter: (a, b) => (a.coquille || "").localeCompare(b.coquille || ""),
    },
    {
      title: "Test radio fait",
      dataIndex: "validation_sortie_coquille",
      sorter: (a, b) => (a.validation_sortie_coquille === b.validation_sortie_coquille ? 0 : a.validation_sortie_coquille ? -1 : 1),
      render: (val, record) => (
        <Checkbox
          checked={val}
          onChange={(e) =>
            handleLocalChange(
              record.key,
              "validation_sortie_coquille",
              e.target.checked,
              record.id
            )
          }
          className="custom-checkbox-border"
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            margin: "0 auto",
            transform: "scale(1.2)",
            cursor: "pointer",
            accentColor: "#007bff",
          }}
        />
      ),
    },
    {
      title: "Commentaire",
      dataIndex: "commentaire",
      className: "comment-cell",
      sorter: (a, b) => (a.commentaire || "").localeCompare(b.commentaire || ""),
      render: (val, record) => (
        <EditableTextCell
          recordId={record.id}
          initialValue={val}
          field="commentaire"
          onPatch={handlePatch}
          rows={2}
        />
      ),
    },
    {
      title: "État",
      dataIndex: "signal",
      sorter: (a, b) => a.signal - b.signal,
      render: (val, record) => {
        const color =
          val === 1 ? "#4CAF50" : val === -1 ? "#FF4D4F" : "#BFBFBF";
        const symbol = val === 1 ? "✓" : val === -1 ? "✗" : " ";

        const handleClick = () => {
          const nextVal = val === 0 ? 1 : val === 1 ? -1 : 0;
          handleLocalChange(record.key, "signal", nextVal, record.id);
        };

        return (
          <div
            onClick={handleClick}
            style={{
              cursor: "pointer",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <div
              style={{
                width: 28,
                height: 28,
                borderRadius: "6px",
                backgroundColor: color,
                color: "white",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                fontWeight: "bold",
                fontSize: "16px",
                userSelect: "none",
                transition: "all 0.3s ease",
              }}
            >
              {symbol}
            </div>
          </div>
        );
      },
    },
  ];

  return (
    <div style={{ padding: "20px", position: "relative" }}>
      {/* Spinner full screen */}
      {loading && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "rgba(255,255,255,0.6)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999,
          }}
        >
          <Spin size="large" tip="Chargement..." />
        </div>
      )}

      <NavigationStepsKeolisNoc selectedKey="coquille_config_dsp" />

      <CoquillesTable url={`${API_URL}/validations/counter/tests_ad/?ot_tag=keolis_noc`} />

      <Input
        placeholder="Rechercher dans toutes les colonnes..."
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
        style={{ 
          marginBottom: "16px", 
          marginTop: "16px",
          width: "100%",
          maxWidth: "500px"
        }}
        allowClear
      />

      <Tooltip title="Rafraîchir les données">
        <Button
          type="primary"
          shape="circle"
          size="large"
          icon={<ReloadOutlined style={{ fontSize: "20px" }} />}
          onClick={fetchData}
          className="app-refresh-button"
          style={{ 
            zIndex: 9999, 
            boxShadow: "0 12px 32px rgba(0, 0, 0, 0.3), 0 4px 12px rgba(0, 0, 0, 0.15)",
            background: "linear-gradient(135deg, #40a9ff 0%, #1890ff 100%)",
            border: "none",
            width: "56px",
            height: "56px"
          }}
        />
      </Tooltip>

      <Table
        columns={columns}
        dataSource={filteredData}
        loading={false} // désactivé car on gère loading global
        expandable={{
          expandedRowRender: (record) => (
            <TableCoquilleExpandable record={record} onPatch={handlePatch} />
          ),
        }}
        bordered
        style={{
          border: "2px solid #bfbfbf",
          borderRadius: "12px",
          overflow: "hidden",
          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
          marginTop: 60, // espace pour le bouton fixe
        }}
        rowClassName={() => "custom-row"}
        pagination={false}
      />

      <div style={{ padding: 20 }}>
        <ModifyItemPage
          apiUrl={`${API_URL}/validations/validation_coquille_config_dsp/`}
          ot_tag="keolis_noc"
          title="Gestion"
          rowKey="id"
          trigger={<Button icon={<EditOutlined />} shape="circle" />}
          columns={[
            { title: "Dsp", dataIndex: "dsp", key: "dsp" },
            { title: "Coquille", dataIndex: "coquille", key: "coquille" },
          ]}
          formFields={[
            { name: "dsp", label: "Dsp", placeholder: "Entrez la Dsp" },
            { name: "coquille", label: "Coquille", placeholder: "Entrez la coquille" },
          ]}
          onRefresh={fetchData}
        />
      </div>
    </div>
  );
};

export default ValidationCoquilleConfigDspKeolis;
